import { isDefined } from "@omni/utils/helper.utils";
import equal from "fast-deep-equal";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import { setNumericFacet } from "@/redux/modules/value/search/search.actions";
import { getBoundsForFacet } from "@/redux/modules/value/search/search.selectors";
import { getInvalidationsForComponent } from "@/utils/search/invalidation.utils";

import { FacetComponentProps } from "../../@types/facet.types";
import { FacetType } from "../../@types/facetType.types";
import { NumericFacetBounds } from "../../@types/numericFacet.types";
import { NumericFacetComponent } from "./numericFacet.component";

const NumericFacet = ({
    facet,
    component
}: FacetComponentProps<typeof FacetType.NUMERIC>) => {
    const min = Math.floor(facet.values.min);
    const max = Math.ceil(facet.values.max);

    const dispatch = useThunkDispatch();

    const currentBounds = useSelector(
        getBoundsForFacet(component, facet.name),
        equal
    );

    const invalidations = useMemo(
        () => getInvalidationsForComponent(component),
        [component]
    );

    const handleOnChangeBounds = (bounds: NumericFacetBounds) => {
        const lowerBound = isDefined(bounds.lowerBound)
            ? Math.max(bounds.lowerBound, min)
            : min;

        const upperBound = isDefined(bounds.upperBound)
            ? Math.min(bounds.upperBound, max)
            : max;

        const fullRange = lowerBound === min && upperBound === max;

        dispatch(
            setNumericFacet(
                component,
                facet.name,
                fullRange
                    ? undefined
                    : { lowerBound: lowerBound, upperBound: upperBound },
                invalidations
            )
        );
    };

    return (
        <NumericFacetComponent
            numericFacetValue={{
                lowerBound: min,
                upperBound: max
            }}
            currentBounds={currentBounds}
            onChangeBounds={handleOnChangeBounds}
        />
    );
};

export { NumericFacet };
